import { create } from 'zustand';

/**
 * Which turns are still running a post-generation validation, published where
 * the rest of the app can see it.
 *
 * The CODESYS closed loop runs after the SSE stream has closed, entirely inside
 * one turn's React state. Everything outside that turn — the sidebar's running
 * icon, the composer, the session row — reads `session.isStreaming`, which went
 * false the moment the stream ended. So for the several minutes the loop can
 * take, the app said the conversation was idle while it was doing the single
 * slowest thing it does, and the only way to find out otherwise was to reload
 * the page.
 *
 * Keyed by message id because that is what the turn knows about itself; callers
 * that need the session map it through the session's message list.
 */
export interface ValidationActivity {
  /** The hook's phase, verbatim, so callers can label the wait. */
  phase: string;
  /** Whether the loop is still in flight. The part everything else acts on. */
  busy: boolean;
  runId?: string;
  updatedAt: number;
}

interface ValidationActivityState {
  activity: Record<string, ValidationActivity>;
  setValidationActivity: (messageId: string, activity: Omit<ValidationActivity, 'updatedAt'>) => void;
  clearValidationActivity: (messageId: string) => void;
}

export const useValidationActivityStore = create<ValidationActivityState>((set) => ({
  activity: {},

  setValidationActivity: (messageId, activity) => set((state) => {
    if (!messageId) return state;
    const previous = state.activity[messageId];
    // Written from an effect that re-runs on every render of the turn, so an
    // unchanged phase must not produce a new object — subscribers re-render on
    // identity, and a turn polling every 2.5s would otherwise re-render the
    // whole sidebar with it.
    if (previous && previous.phase === activity.phase
      && previous.busy === activity.busy
      && previous.runId === activity.runId) {
      return state;
    }
    return {
      activity: { ...state.activity, [messageId]: { ...activity, updatedAt: Date.now() } },
    };
  }),

  clearValidationActivity: (messageId) => set((state) => {
    if (!messageId || !(messageId in state.activity)) return state;
    const next = { ...state.activity };
    delete next[messageId];
    return { activity: next };
  }),
}));
